import { animate, animateChild, query, stagger, style, transition, trigger } from '@angular/animations';
import { CommonModule, DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, Input, OnChanges } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AbsenceStatus } from 'src/app/models/enums/absence-status';

export type StepState = 'done' | 'wait' | 'inactiveWait' | 'rejected' | 'cancelled';

export interface ApprovalStep {
  key: string;
  label: string;
  state: StepState;
  tooltip?: string | null;
}

export interface FinalDecisionMeta {
  acceptedBy?: string;
  acceptedOn?: Date | string;
  rejectedBy?: string;
  rejectedOn?: Date | string;
  statusName?: string;
}

@Component({
  selector: 'app-approval-progress',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, TranslateModule],
  providers: [DatePipe],
  templateUrl: './approval-progress.component.html',
  styleUrl: './approval-progress.component.scss',
  animations: [
    trigger('list', [
      transition(':enter, * => *', [
        query('@step', stagger(80, animateChild()), { optional: true }),
        query('@connector', stagger(80, animateChild()), { optional: true })
      ])
    ]),
    trigger('step', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(6px) scale(0.98)' }),
        animate('240ms cubic-bezier(0.2, 0, 0, 1)',
          style({ opacity: 1, transform: 'none' }))
      ]),
      transition(':leave', [
        animate('180ms ease-in',
          style({ opacity: 0, transform: 'translateY(-6px) scale(0.98)' }))
      ])
    ]),
    trigger('connector', [
      transition(':enter', [
        style({ transform: 'scaleX(0)' }),
        animate('300ms 80ms ease-out', style({ transform: 'scaleX(1)' }))
      ]),
      transition(':leave', [
        animate('160ms ease-in', style({ transform: 'scaleX(0)' }))
      ])
    ])
  ]
})
export class ApprovalProgressComponent implements OnChanges, AfterViewInit {
  @Input() statusId!: number;
  @Input() intermediateSteps: ApprovalStep[] = [];
  @Input() finalMeta: FinalDecisionMeta;

  steps: ApprovalStep[] = [];
  private readonly datePipe = inject(DatePipe);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  public initialRender = true;

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initialRender = false;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(): void {
    this.steps = this.extendWithIntermediates(
      this.buildBasicSteps(this.statusId, this.finalMeta),
      this.intermediateSteps
    );
  }

  private buildBasicSteps(statusId: number | null, finalMeta?: FinalDecisionMeta): ApprovalStep[] {
    const createdState: StepState =
      !statusId || statusId === AbsenceStatus.Draft ? 'wait' : 'done';

    const finalState = this.getFinalStepState(statusId);

    let finalTooltip: string | null = null;
    if (finalState === 'done' && finalMeta?.acceptedBy && finalMeta?.acceptedOn) {
      finalTooltip = this.translateService.instant('AbsenceStatusTooltip.AcceptedBy', {
        userName: finalMeta.acceptedBy,
        date: this.formatDate(finalMeta.acceptedOn)
      });
    } else if (finalState === 'rejected' && finalMeta?.rejectedBy && finalMeta?.rejectedOn) {
      finalTooltip = this.translateService.instant('AbsenceStatusTooltip.RejectedBy', {
        userName: finalMeta.rejectedBy,
        date: this.formatDate(finalMeta.rejectedOn)
      });
    }
    return [
      { key: 'created', label: 'approval-progress.created', state: createdState },
      { key: 'final', label: this.getFinalStepLabel(statusId, finalMeta), state: finalState, tooltip: finalTooltip }
    ];
  }

  private getFinalStepState(statusId: number) {
    switch (statusId) {
      case AbsenceStatus.PendingApproval:
        return 'wait';
      case AbsenceStatus.Accepted:
        return 'done';
      case AbsenceStatus.Rejected:
        return 'rejected';
      case AbsenceStatus.Cancelled:
        return 'cancelled';
      case AbsenceStatus.Draft:
      default:
        return 'inactiveWait';
    }
  }

  private getFinalStepLabel(statusId: number, finalMeta?: FinalDecisionMeta) {
    switch (statusId) {
      case AbsenceStatus.Accepted:
      case AbsenceStatus.Rejected:
      case AbsenceStatus.Cancelled:
        return finalMeta?.statusName;
      default:
        return 'approval-progress.acceptedFinal';
    }
  }

  private extendWithIntermediates(basic: ApprovalStep[], intermediates: ApprovalStep[]): ApprovalStep[] {
    const safeIntermediates = (intermediates ?? []).map(s => ({
      ...s,
      state: this.normalizeState(s.state)
    }));

    const created = basic.find(s => s.key === 'created');
    const final = basic.find(s => s.key === 'final');

    const middle = safeIntermediates.filter(s => s.key !== 'created' && s.key !== 'final');

    return [
      ...(created ? [created] : []),
      ...middle,
      ...(final ? [final] : [])
    ];
  }

  private normalizeState(state: StepState): StepState {
    switch (state) {
      case 'done':
      case 'wait':
      case 'inactiveWait':
      case 'rejected':
      case 'cancelled':
        return state;
      default:
        return 'inactiveWait';
    }
  }

  private formatDate = (d: Date | string) =>
    this.datePipe.transform(d, 'yyyy-MM-dd HH:mm');

  isBaseStep(key: string): boolean {
    return key === 'created' || key === 'final';
  }

  trackByKey(index: number, s: { key: string }): string {
    return s.key;
  }
}
